import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CATEGORIES, SOURCES, LEAD_TYPES, STAGES, ROLE_LABEL, PRIORITIES } from "@/lib/constants";
import { StagePill } from "@/components/StagePill";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Trash2, UserCheck, UserPlus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY = {
  type: "Company",
  name: "",
  company: "",
  designation: "",
  email: "",
  mobile: "",
  website: "",
  linkedin_url: "",
  location: "",
  category: "Other",
  source: "Manual",
  stage: "New Lead",
  priority: "Medium",
  notes: "",
  next_follow_up: "",
};

const Input = (props) => (
  <input
    {...props}
    className={
      "w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm " +
      "focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none " +
      "transition-all placeholder:text-muted-foreground " + (props.className || "")
    }
  />
);

const Field = ({ label, children, required }) => (
  <div>
    <label className="text-label block mb-1.5">
      {label} {required && <span className="text-destructive">*</span>}
    </label>
    {children}
  </div>
);

export function LeadDrawer({ open, onClose, leadId, onSaved, users }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState(EMPTY);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stageChange, setStageChange] = useState(null);

  const isNew = !leadId;
  const isMaster = user?.role === "master_admin";
  const isAdmin = user?.role === "admin";
  const canAssign = isMaster || isAdmin;

  useEffect(() => {
    if (!open) return;
    setTab("details");
    setStageChange(null);
    if (!leadId) {
      setForm(EMPTY);
      setActivities([]);
      return;
    }
    api.get(`/leads/${leadId}`).then(({ data }) => {
      setForm({ ...EMPTY, ...data.lead });
      setActivities(data.activities || []);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [open, leadId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...form };
      // Strip empty string -> remove keys to avoid email validation on empty
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      let saved;
      if (isNew) {
        saved = (await api.post("/leads", payload)).data;
        toast.success(`Lead ${saved.lead_no} created`);
      } else {
        saved = (await api.patch(`/leads/${leadId}`, payload)).data;
        toast.success("Lead updated");
      }
      onSaved?.(saved);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const changeStage = async () => {
    if (!stageChange) return;
    setLoading(true);
    try {
      const payload = {
        stage: stageChange.stage,
        notes: stageChange.notes,
        next_follow_up: stageChange.next_follow_up,
        lost_reason: stageChange.lost_reason,
      };
      const saved = (await api.post(`/leads/${leadId}/stage`, payload)).data;
      toast.success(`Moved to ${saved.stage}`);
      setForm((f) => ({ ...f, ...saved }));
      setStageChange(null);
      const { data } = await api.get(`/leads/${leadId}`);
      setActivities(data.activities || []);
      onSaved?.(saved);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const assign = async (owner_id) => {
    setLoading(true);
    try {
      const saved = (await api.post(`/leads/${leadId}/assign`, { owner_id })).data;
      toast.success("Lead assigned");
      setForm((f) => ({ ...f, ...saved }));
      onSaved?.(saved);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead? This action is logged.")) return;
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      onSaved?.({ deleted: true, id: leadId });
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const convertToCustomer = async () => {
    try {
      const { data } = await api.post(`/leads/${leadId}/convert-to-customer`);
      toast.success(`Converted to customer ${data.customer_no}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="lead-drawer"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            {isNew ? "Create lead" : (
              <>
                <span className="mono text-xs text-muted-foreground">{form.lead_no}</span>
                <span className="truncate">{form.name || "Lead"}</span>
              </>
            )}
          </SheetTitle>
          <SheetDescription>
            {isNew ? "Capture a new lead manually." : "View, edit, change stage or reassign."}
          </SheetDescription>
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList data-testid="lead-tabs">
            <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            {!isNew && <TabsTrigger value="stage" data-testid="tab-stage">Stage</TabsTrigger>}
            {!isNew && canAssign && <TabsTrigger value="assign" data-testid="tab-assign">Assign</TabsTrigger>}
            {!isNew && <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>}
          </TabsList>

          <TabsContent value="details" className="mt-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <Select value={form.type} onValueChange={set("type")}>
                  <SelectTrigger data-testid="lead-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Stage">
                <Select value={form.stage} onValueChange={set("stage")} disabled={isNew}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Name" required>
              <Input value={form.name} onChange={set("name")} placeholder="Jane Doe"
                data-testid="lead-name-input" />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Company">
                <Input value={form.company || ""} onChange={set("company")} placeholder="Acme Inc."
                  data-testid="lead-company-input" />
              </Field>
              <Field label="Designation">
                <Input value={form.designation || ""} onChange={set("designation")} placeholder="Head of Sales" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <Input type="email" value={form.email || ""} onChange={set("email")} placeholder="jane@acme.com"
                  data-testid="lead-email-input" />
              </Field>
              <Field label="Mobile">
                <Input value={form.mobile || ""} onChange={set("mobile")} placeholder="+1 555 123 4567"
                  data-testid="lead-mobile-input" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Website">
                <Input value={form.website || ""} onChange={set("website")} placeholder="https://acme.com" />
              </Field>
              <Field label="LinkedIn URL">
                <Input value={form.linkedin_url || ""} onChange={set("linkedin_url")}
                  placeholder="https://linkedin.com/in/…" data-testid="lead-linkedin-input" />
              </Field>
            </div>

            <Field label="Location">
              <Input value={form.location || ""} onChange={set("location")} placeholder="San Francisco, US" />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <Select value={form.category} onValueChange={set("category")}>
                  <SelectTrigger data-testid="lead-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Source">
                <Select value={form.source} onValueChange={set("source")}>
                  <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Priority">
              <Select value={form.priority || "Medium"} onValueChange={set("priority")}>
                <SelectTrigger data-testid="lead-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Next Follow-Up">
              <Input
                type="datetime-local"
                value={form.next_follow_up ? form.next_follow_up.slice(0, 16) : ""}
                onChange={(e) => set("next_follow_up")(e.target.value)}
                data-testid="lead-followup-input"
              />
            </Field>

            <Field label="Notes">
              <textarea
                value={form.notes || ""}
                onChange={set("notes")}
                rows={3}
                className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none"
              />
            </Field>
          </TabsContent>

          {!isNew && (
            <TabsContent value="stage" className="mt-5 space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Current:</span>
                <StagePill stage={form.stage} />
              </div>
              <Field label="Move to stage">
                <Select
                  value={stageChange?.stage || ""}
                  onValueChange={(s) => setStageChange({ stage: s, notes: "", next_follow_up: "", lost_reason: "" })}
                >
                  <SelectTrigger data-testid="stage-change-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              {stageChange && (
                <>
                  {!["Won", "Lost"].includes(stageChange.stage) && (
                    <Field label="Next Follow-Up" required>
                      <Input
                        type="datetime-local"
                        value={stageChange.next_follow_up}
                        onChange={(e) => setStageChange((c) => ({ ...c, next_follow_up: e.target.value }))}
                        data-testid="stage-followup-input"
                      />
                    </Field>
                  )}
                  {stageChange.stage === "Lost" && (
                    <Field label="Lost reason">
                      <Input
                        value={stageChange.lost_reason}
                        onChange={(e) => setStageChange((c) => ({ ...c, lost_reason: e.target.value }))}
                      />
                    </Field>
                  )}
                  <Field label="Note">
                    <textarea
                      rows={2}
                      value={stageChange.notes}
                      onChange={(e) => setStageChange((c) => ({ ...c, notes: e.target.value }))}
                      className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm"
                    />
                  </Field>
                  <button
                    onClick={changeStage}
                    disabled={loading}
                    data-testid="stage-change-submit"
                    className="text-white text-sm font-medium rounded-sm py-2 px-4 hover:opacity-90"
                    style={{ backgroundColor: "var(--theme-color)" }}
                  >
                    Move to {stageChange.stage}
                  </button>
                </>
              )}
            </TabsContent>
          )}

          {!isNew && canAssign && (
            <TabsContent value="assign" className="mt-5 space-y-4">
              <div className="text-sm text-muted-foreground">
                Current owner:{" "}
                <span className="text-foreground font-medium">
                  {users.find((u) => u.id === form.owner_id)?.full_name || "Unassigned"}
                </span>
              </div>
              <Field label="Assign to">
                <Select
                  value={form.owner_id || ""}
                  onValueChange={(v) => assign(v)}
                >
                  <SelectTrigger data-testid="assign-select"><SelectValue placeholder="Select user…" /></SelectTrigger>
                  <SelectContent>
                    {users
                      .filter((u) => u.is_active && u.role !== "master_admin")
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.full_name} · {ROLE_LABEL[u.role]}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </Field>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <UserCheck className="w-3.5 h-3.5" /> Only Admin or Master Admin can reassign.
              </p>
            </TabsContent>
          )}

          {!isNew && (
            <TabsContent value="activity" className="mt-5">
              <div className="space-y-3">
                {activities.length === 0 && (
                  <p className="text-sm text-muted-foreground">No activity yet.</p>
                )}
                {activities.map((a) => (
                  <div key={a.id} className="border-l-2 border-border pl-3 py-1" data-testid={`activity-${a.id}`}>
                    <div className="text-sm">
                      <span className="font-medium">{a.user_name}</span>{" "}
                      <span className="text-muted-foreground">{a.action.replace("_", " ")}</span>
                      {a.stage_to && (
                        <> → <StagePill stage={a.stage_to} className="ml-1" /></>
                      )}
                    </div>
                    {a.notes && <p className="text-xs text-muted-foreground mt-1">{a.notes}</p>}
                    <p className="text-[10px] text-muted-foreground mt-1 mono">
                      {new Date(a.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>

        <SheetFooter className="mt-6 flex-row justify-between sm:justify-between">
          <div className="flex items-center gap-2">
            {!isNew && isMaster && (
              <button
                onClick={remove}
                data-testid="delete-lead-btn"
                className="text-sm text-destructive hover:bg-destructive/10 rounded-sm px-3 py-1.5 flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
            {!isNew && canAssign && form.stage === "Won" && (
              <button
                onClick={convertToCustomer}
                data-testid="convert-customer-btn"
                className="text-sm border border-border hover:bg-secondary rounded-sm px-3 py-1.5 flex items-center gap-1.5"
              >
                <UserPlus className="w-4 h-4" /> Convert to Customer
              </button>
            )}
          </div>
          {tab === "details" && (
            <button
              onClick={save}
              disabled={loading || !form.name}
              data-testid="save-lead-btn"
              className="text-white text-sm font-medium rounded-sm py-2 px-4 hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
              style={{ backgroundColor: "var(--theme-color)" }}
            >
              <Save className="w-4 h-4" /> {isNew ? "Create lead" : "Save"}
            </button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
